import { create } from "zustand"

import type { CanvasVersion } from "@/components/canvas-editor/version-history-dropdown"

export type OpenCanvasPayload = {
  documentId?: string
  messageId?: string
  sessionId?: string
  sessionFilenames?: string[]
  /** When set, canvas session-images are fetched from this base (a saved
   * document's document-content endpoint) instead of /session-content. */
  imageContentBaseUrl?: string
  activeDocumentCanEdit?: boolean
  title?: string
  content: string
  versions?: CanvasVersion[]
  selectedModelId?: string
  etag?: string | null
  lastSavedAt?: string | null
  onMessageUpdate?: (() => void) | null
  /**
   * When `true`, the canvas autosave path is fully suppressed — no API calls
   * are made and no "saving / saved / error" status is emitted.  Defaults to
   * `false`, which preserves the existing document/message save behaviour for
   * all other callers.  Set to `true` for in-memory (artifact) canvases that
   * have no backing server resource.
   */
  disablePersistence?: boolean
}

export type PendingRemoteUpdate = {
  documentId: string
  changeSummary?: string
  previousContent: string
  previousVersion: number | null
  nextContent: string
  nextVersion: number | null
  nextTitle?: string
  nextVersions: CanvasVersion[]
  nextEtag?: string | null
  nextUpdatedAt?: string | null
}

type CanvasState = {
  isDirty: boolean
  documentId: string | null
  messageId: string | null
  sessionId: string | null
  sessionFilenames: string[]
  imageContentBaseUrl: string | null
  activeDocumentCanEdit: boolean
  title: string
  content: string
  versions: CanvasVersion[]
  loadRevision: number
  selectedModelId: string | null
  etag: string | null
  lastSavedAt: string | null
  autosaveStatus: "idle" | "saving" | "saved" | "error"
  activeSelectionText: string
  activeSelectionStart: number | null
  activeSelectionEnd: number | null
  pendingRemoteUpdate: PendingRemoteUpdate | null
  onMessageUpdate: (() => void) | null
  /** See `OpenCanvasPayload.disablePersistence`. */
  disablePersistence: boolean

  openCanvas: (payload: OpenCanvasPayload) => void
  closeCanvas: () => void
  clearCanvas: () => void
  setContent: (content: string) => void
  setTitle: (title: string) => void
  setIsDirty: (isDirty: boolean) => void
  setVersions: (versions: CanvasVersion[]) => void
  appendVersion: (version: CanvasVersion) => void
  setEtag: (etag: string | null) => void
  setLastSavedAt: (timestamp: string | null) => void
  setAutosaveStatus: (status: "idle" | "saving" | "saved" | "error") => void
  setActiveSelection: (
    selectionText: string,
    selectionStart: number | null,
    selectionEnd: number | null
  ) => void
  clearActiveSelection: () => void
  stageRemoteUpdate: (update: PendingRemoteUpdate) => void
  dismissRemoteUpdate: () => void
  applyRemoteUpdate: () => void
}

const defaultCanvasState: Omit<
  CanvasState,
  | "openCanvas"
  | "closeCanvas"
  | "clearCanvas"
  | "setContent"
  | "setTitle"
  | "setIsDirty"
  | "setVersions"
  | "appendVersion"
  | "setEtag"
  | "setLastSavedAt"
  | "setAutosaveStatus"
  | "setActiveSelection"
  | "clearActiveSelection"
  | "stageRemoteUpdate"
  | "dismissRemoteUpdate"
  | "applyRemoteUpdate"
> = {
  isDirty: false,
  documentId: null,
  messageId: null,
  sessionId: null,
  sessionFilenames: [],
  imageContentBaseUrl: null,
  activeDocumentCanEdit: true,
  title: "Canvas",
  content: "",
  versions: [],
  loadRevision: 0,
  selectedModelId: null,
  etag: null,
  lastSavedAt: null,
  autosaveStatus: "idle",
  activeSelectionText: "",
  activeSelectionStart: null,
  activeSelectionEnd: null,
  pendingRemoteUpdate: null,
  onMessageUpdate: null,
  disablePersistence: false,
}

export const useCanvasStore = create<CanvasState>((set, get) => ({
  ...defaultCanvasState,

  openCanvas: ({
    documentId,
    messageId,
    sessionId,
    sessionFilenames,
    imageContentBaseUrl,
    activeDocumentCanEdit,
    title,
    content,
    versions,
    selectedModelId,
    etag,
    lastSavedAt,
    onMessageUpdate,
    disablePersistence,
  }) => {
    set({
      ...defaultCanvasState,
      documentId: documentId ?? null,
      messageId: messageId ?? null,
      sessionId: sessionId ?? null,
      sessionFilenames: sessionFilenames ?? [],
      imageContentBaseUrl: imageContentBaseUrl ?? null,
      activeDocumentCanEdit: activeDocumentCanEdit ?? true,
      title: title ?? "Canvas",
      content,
      versions: versions ?? [],
      loadRevision: get().loadRevision + 1,
      selectedModelId: selectedModelId ?? null,
      etag: etag ?? null,
      lastSavedAt: lastSavedAt ?? null,
      onMessageUpdate: onMessageUpdate ?? null,
      disablePersistence: disablePersistence ?? false,
    })
  },

  closeCanvas: () => {
    set((state) => ({
      ...defaultCanvasState,
      loadRevision: state.loadRevision,
    }))
  },

  clearCanvas: () => {
    set((state) => ({
      ...defaultCanvasState,
      loadRevision: state.loadRevision,
    }))
  },

  setContent: (content) => set({ content }),
  setTitle: (title) => set({ title }),
  setIsDirty: (isDirty) => set({ isDirty }),
  setVersions: (versions) => set({ versions }),
  appendVersion: (version) => set((state) => ({ versions: [...state.versions, version] })),
  setEtag: (etag) => set({ etag }),
  setLastSavedAt: (lastSavedAt) => set({ lastSavedAt }),
  setAutosaveStatus: (autosaveStatus) => set({ autosaveStatus }),
  setActiveSelection: (activeSelectionText, activeSelectionStart, activeSelectionEnd) =>
    set({ activeSelectionText, activeSelectionStart, activeSelectionEnd }),
  clearActiveSelection: () =>
    set({ activeSelectionText: "", activeSelectionStart: null, activeSelectionEnd: null }),
  stageRemoteUpdate: (pendingRemoteUpdate) => set({ pendingRemoteUpdate }),
  dismissRemoteUpdate: () => set({ pendingRemoteUpdate: null }),
  applyRemoteUpdate: () =>
    set((state) => {
      const pending = state.pendingRemoteUpdate
      if (!pending) {
        return {}
      }
      return {
        title: pending.nextTitle ?? state.title,
        content: pending.nextContent,
        versions: pending.nextVersions,
        etag: pending.nextEtag ?? state.etag,
        lastSavedAt: pending.nextUpdatedAt ?? state.lastSavedAt,
        isDirty: false,
        autosaveStatus: "saved",
        loadRevision: state.loadRevision + 1,
        pendingRemoteUpdate: null,
      }
    }),
}))
